import React from 'react';
import { Bloodwork } from '../../models/Bloodwork';

import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from "highcharts/highcharts-more";
import SolidGuage from "highcharts/modules/solid-gauge";
import { ProbabilityCategory } from '../../models/ProbabilityCategory';
import { MUTED_PALETTE } from './chartPalette';
HighchartsMore(Highcharts);
SolidGuage(Highcharts);

type Props = {
    chartContainerId: string;
    bloodwork: Bloodwork;
    text: string;
    probability: number;
    category: ProbabilityCategory;
    /** Score values to label on the arc (e.g. zone boundaries). Off by default. */
    ticks?: number[];
    /**
     * The arc's colored zones, when the panel's ladder is not the likelihood
     * ladder. Defaults to 0/50/70/90, which is what every likelihood gauge
     * uses and what this component drew before the prop existed.
     *
     * Added 2026-10-26 for the LP-IR gauge, whose zones are PRINTED BY THE LAB
     * at 27/45/63 and are not ours to choose.
     */
    bands?: Array<{ from: number; to: number; color: string }>;
    /**
     * Replaces the "<category> (<probability>%)" reading under the stem.
     *
     * ⚠ THE DEFAULT READING HAS A PERCENT SIGN BAKED IN, which is correct for
     * a gauge that INFERS a likelihood and wrong for one that reports a
     * measured index: LP-IR 63 is a score of 63, not "63% likely". Pass this
     * to say what the number actually is.
     */
    readout?: string;
};

type State = {
    bloodwork: Bloodwork;
}

export class LabsmartsGuage extends React.Component<Props, State> {
    componentWillReceiveProps(nextProps) {
        this.setState({
            bloodwork: nextProps.bloodwork
        });
    }

    createOptions() {
        const { bloodwork, text, probability, category, ticks, bands, readout } = this.props;

        if (!bloodwork) {
            return null;
        }

        const showTicks = !!(ticks && ticks.length);

        const { green: GREEN, yellow: YELLOW, orange: ORANGE, red: RED } = MUTED_PALETTE;
        const options: Highcharts.Options = {
            title: null,
            chart: {
                type: 'solidgauge',
                height: 200,
                width: 260,
                margin: [-20, 0, -40, 0],
                backgroundColor: null
            },
            pane: {
                center: ['50%', '60%'],
                size: '100%',
                startAngle: -90,
                endAngle: 90,
                background: [{
                    innerRadius: '60%',
                    outerRadius: '100%',
                    shape: 'arc'
                }]
            },
            yAxis: {
                min: 0,
                max: 100,
                lineWidth: 0,
                // No tick dashes anywhere — major OR minor — just the number labels
                // at the zone boundaries. (tickWidth:0 alone leaves minor ticks drawn.)
                tickWidth: 0,
                tickLength: 0,
                minorTickWidth: 0,
                minorTickLength: 0,
                // Numbers sit just inside the colored ring, so they never widen the
                // 260px chart the way outside labels (0 / 100 at the arc ends) would.
                tickPositions: showTicks ? ticks : [],
                labels: {
                    enabled: showTicks,
                    distance: -15,
                    style: { fontSize: '10.5px', color: '#3a3a3a', fontWeight: '600', textOutline: '2px #ffffff' },
                },
                plotBands: (bands && bands.length
                    ? bands
                    : [
                        { color: GREEN, from: 0, to: 50 },
                        { color: YELLOW, from: 50, to: 70 },
                        { color: ORANGE, from: 70, to: 90 },
                        { color: RED, from: 90, to: 100 },
                    ]
                ).map(band => ({
                    ...band,
                    innerRadius: '60%',
                    outerRadius: '100%',
                }))
            },
            series: [{
                type: 'gauge',
                data: [probability],
                dataLabels: {
                    useHTML: true,
                    format: `<div class="anemiaStatusGuageLabelContainer">${text}<br/>
                                <div class="anemiaStatusGuageLabel">
                                    ${readout || `${category} (${probability}%)`}
                                </div>
                             </div>`,
                    borderWidth: 0
                }
            }],
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            tooltip: {
                enabled: false
            }
        };
        return options;
    }

    render() {
        const { chartContainerId } = this.props;

        return (
            <HighchartsReact 
                highcharts={Highcharts}
                options={this.createOptions()}
                containerProps = { { id: chartContainerId } }
            />
        );
    }
}